import { lazy } from 'react';

// project import
import Loadable from '../components/Loadable';
import MainLayout from '../layout/MainLayout';
import ProtectedRoute from '../components/ProtectedRoute';
import { PERMISSIONS } from '../utils/permissions';

// render - dashboard
const DashboardDefault = Loadable(lazy(() => import('../pages/Dashboard')));
const ShadcnTest = Loadable(lazy(() => import('../pages/ShadcnTest')));
const ShadcnTestInline = Loadable(lazy(() => import('../pages/ShadcnTestInline')));
const TailwindCheck = Loadable(lazy(() => import('../pages/TailwindCheck')));

// render - patients
const PatientList = Loadable(lazy(() => import('../pages/patients/PatientList')));
const PatientRegistration = Loadable(lazy(() => import('../pages/patients/PatientRegistration')));
const PatientDetails = Loadable(lazy(() => import('../pages/patients/PatientDetails')));
const PatientEdit = Loadable(lazy(() => import('../pages/patients/PatientEdit')));
const PatientReview = Loadable(lazy(() => import('../pages/patients/PatientReview')));
const PatientAssessment = Loadable(lazy(() => import('../pages/patients/PatientAssessment')));

// render - caregivers
const CaregiverList = Loadable(lazy(() => import('../pages/caregivers/CaregiverList')));
const CaregiverRegistration = Loadable(lazy(() => import('../pages/caregivers/CaregiverRegistration')));
const CaregiverDetails = Loadable(lazy(() => import('../pages/caregivers/CaregiverDetails')));
const CaregiverEdit = Loadable(lazy(() => import('../pages/caregivers/CaregiverEdit')));

// render - financial
const InvoiceManagement = Loadable(lazy(() => import('../pages/financial/InvoiceManagement')));
const InvoiceLogs = Loadable(lazy(() => import('../pages/financial/InvoiceLogs')));
const InvoiceCreation = Loadable(lazy(() => import('../pages/financial/InvoiceCreation')));
const BillingTest = Loadable(lazy(() => import('../pages/financial/BillingTest')));
const BillingConfigTest = Loadable(lazy(() => import('../pages/financial/BillingConfigTest')));
const PaymentTrackingDashboard = Loadable(lazy(() => import('../pages/financial/PaymentTrackingDashboard')));
const GSTComplianceReporting = Loadable(lazy(() => import('../pages/financial/GSTComplianceReporting')));
const PatientBillingConfiguration = Loadable(lazy(() => import('../pages/billing/PatientBillingConfiguration')));
const CaregiverWageConfiguration = Loadable(lazy(() => import('../pages/financial/CaregiverWageConfiguration')));
const PayrollCalculationProcessing = Loadable(lazy(() => import('../pages/financial/PayrollCalculationProcessing')));
const PayrollProcessing = Loadable(lazy(() => import('../pages/financial/PayrollProcessing')));
const PayrollRecords = Loadable(lazy(() => import('../pages/financial/PayrollRecords')));
const PaySlipGeneration = Loadable(lazy(() => import('../pages/financial/PaySlipGeneration')));
const PatientAccountManagement = Loadable(lazy(() => import('../pages/financial/PatientAccountManagement')));
const HolidayCalendar = Loadable(lazy(() => import('../pages/financial/HolidayCalendar')));
const SubscriptionBilling = Loadable(lazy(() => import('../pages/financial/SubscriptionBilling')));


// render - profile
const UserProfile = Loadable(lazy(() => import('../pages/profile/UserProfile')));

// render - users
const UserManagement = Loadable(lazy(() => import('../pages/users/UserManagement')));
const UserDetails = Loadable(lazy(() => import('../pages/users/UserDetails')));
const UserEdit = Loadable(lazy(() => import('../pages/users/UserEdit')));

// render - shifts
const ShiftCalendar = Loadable(lazy(() => import('../pages/shifts/ShiftCalendar')));
const ShiftCreate = Loadable(lazy(() => import('../pages/shifts/ShiftCreate')));
const ShiftEdit = Loadable(lazy(() => import('../pages/shifts/ShiftEdit')));
const ShiftMonitoring = Loadable(lazy(() => import('../pages/shifts/ShiftMonitoring')));
const TimeTrackingDashboard = Loadable(lazy(() => import('../pages/shifts/TimeTrackingDashboard')));
const AttendanceDashboard = Loadable(lazy(() => import('../pages/shifts/AttendanceDashboard')));
const AttendanceRecords = Loadable(lazy(() => import('../pages/shifts/AttendanceRecords')));

// render - careplans
const CarePlanDetails = Loadable(lazy(() => import('../pages/careplans/CarePlanDetails')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: <MainLayout />,
  children: [
    {
      path: '',
      element: (
        <ProtectedRoute>
          <DashboardDefault />
        </ProtectedRoute>
      )
    },
    {
      path: 'dashboard',
      element: (
        <ProtectedRoute>
          <DashboardDefault />
        </ProtectedRoute>
      )
    },
    {
      path: 'shadcn-test',
      element: (
        <ProtectedRoute>
          <ShadcnTest />
        </ProtectedRoute>
      )
    },
    {
      path: 'shadcn-inline',
      element: (
        <ProtectedRoute>
          <ShadcnTestInline />
        </ProtectedRoute>
      )
    },
    {
      path: 'tailwind-check',
      element: (
        <ProtectedRoute>
          <TailwindCheck />
        </ProtectedRoute>
      )
    },
    {
      path: 'patients',
      children: [
        {
          path: 'list',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.PATIENT_READ]}>
              <PatientList />
            </ProtectedRoute>
          )
        },
        {
          path: 'register',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.PATIENT_CREATE]}>
              <PatientRegistration />
            </ProtectedRoute>
          )
        },
        {
          path: ':id',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.PATIENT_READ]}>
              <PatientDetails />
            </ProtectedRoute>
          )
        },
        {
          path: ':id/edit',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.PATIENT_UPDATE]}>
              <PatientEdit />
            </ProtectedRoute>
          )
        },
        {
          path: ':id/review',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.PATIENT_UPDATE]}>
              <PatientReview />
            </ProtectedRoute>
          )
        },
        {
          path: ':id/assessment',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.PATIENT_UPDATE]}>
              <PatientAssessment />
            </ProtectedRoute>
          )
        }
      ]
    },
    {
      path: 'caregivers',
      children: [
        {
          path: 'list',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.CAREGIVER_READ]}>
              <CaregiverList />
            </ProtectedRoute>
          )
        },
        {
          path: 'register',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.CAREGIVER_CREATE]}>
              <CaregiverRegistration />
            </ProtectedRoute>
          )
        },
        {
          path: ':id',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.CAREGIVER_READ]}>
              <CaregiverDetails />
            </ProtectedRoute>
          )
        },
        {
          path: ':id/edit',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.CAREGIVER_UPDATE]}>
              <CaregiverEdit />
            </ProtectedRoute>
          )
        }
      ]
    },
    {
      path: 'financial',
      children: [
        {
          path: 'invoices',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.BILLING_READ]} requiredFeature="billingModule">
              <InvoiceManagement />
            </ProtectedRoute>
          )
        },
        {
          path: 'invoice-logs',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.BILLING_READ]} requiredFeature="billingModule">
              <InvoiceLogs />
            </ProtectedRoute>
          )
        },
        {
          path: 'create-invoice',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.BILLING_MANAGE]} requiredFeature="billingModule">
              <InvoiceCreation />
            </ProtectedRoute>
          )
        },
        {
          path: 'billing-test',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN" requiredFeature="billingModule">
              <BillingTest />
            </ProtectedRoute>
          )
        },
        {
          path: 'billing-config-test',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN" requiredFeature="billingModule">
              <BillingConfigTest />
            </ProtectedRoute>
          )
        },
        {
          path: 'billing',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN" requiredFeature="billingModule">
              <PatientBillingConfiguration />
            </ProtectedRoute>
          )
        },
        {
          path: 'payment-tracking',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.BILLING_READ]} requiredFeature="billingModule">
              <PaymentTrackingDashboard />
            </ProtectedRoute>
          )
        },
        {
          path: 'gst-compliance',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.BILLING_MANAGE]} requiredFeature="billingModule">
              <GSTComplianceReporting />
            </ProtectedRoute>
          )
        },
        {
          path: 'wage-config',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN" requiredFeature="payrollIntegration">
              <CaregiverWageConfiguration />
            </ProtectedRoute>
          )
        },
        {
          path: 'holidays',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN" requiredFeature="payrollIntegration">
              <HolidayCalendar />
            </ProtectedRoute>
          )
        },
        {
          path: 'payroll',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN" requiredFeature="payrollIntegration">
              <PayrollCalculationProcessing />
            </ProtectedRoute>
          )
        },
        {
          path: 'payroll-processing',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN" requiredFeature="payrollIntegration">
              <PayrollProcessing />
            </ProtectedRoute>
          )
        },
        {
          path: 'payroll-records',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN" requiredFeature="payrollIntegration">
              <PayrollRecords />
            </ProtectedRoute>
          )
        },
        {
          path: 'payslips',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN" requiredFeature="payrollIntegration">
              <PaySlipGeneration />
            </ProtectedRoute>
          )
        },
        {
          path: 'patient-account-management',
          element: (
            <ProtectedRoute permissions={[PERMISSIONS.BILLING_READ]} requiredFeature="billingModule">
              <PatientAccountManagement />
            </ProtectedRoute>
          )
        },
        {
          path: 'subscription',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN">
              <SubscriptionBilling />
            </ProtectedRoute>
          )
        }
      ]
    },
    {
      path: 'profile',
      element: (
        <ProtectedRoute>
          <UserProfile />
        </ProtectedRoute>
      )
    },
    {
      path: 'users',
      children: [
        {
          path: 'management',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN">
              <UserManagement />
            </ProtectedRoute>
          )
        },
        {
          path: ':id',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN">
              <UserDetails />
            </ProtectedRoute>
          )
        },
        {
          path: ':id/edit',
          element: (
            <ProtectedRoute requiredRole="AGENCY_ADMIN">
              <UserEdit />
            </ProtectedRoute>
          )
        }
      ]
    },
    {
      path: 'shifts',
      children: [
        {
          path: 'calendar',
          element: (
            <ProtectedRoute>
              <ShiftCalendar />
            </ProtectedRoute>
          )
        },
        {
          path: 'create',
          element: (
            <ProtectedRoute>
              <ShiftCreate />
            </ProtectedRoute>
          )
        },
        {
          path: 'edit/:id',
          element: (
            <ProtectedRoute>
              <ShiftEdit />
            </ProtectedRoute>
          )
        },
        {
          path: 'monitoring',
          element: (
            <ProtectedRoute>
              <ShiftMonitoring />
            </ProtectedRoute>
          )
        },
        {
          path: 'time-tracking',
          element: (
            <ProtectedRoute>
              <TimeTrackingDashboard />
            </ProtectedRoute>
          )
        },
        {
          path: 'attendance',
          element: (
            <ProtectedRoute>
              <AttendanceDashboard />
            </ProtectedRoute>
          )
        },
        {
          path: 'attendance-records',
          element: (
            <ProtectedRoute>
              <AttendanceRecords />
            </ProtectedRoute>
          )
        }
      ]
    },
    {
      path: 'careplans',
      children: [
        {
          path: ':id',
          element: (
            <ProtectedRoute>
              <CarePlanDetails />
            </ProtectedRoute>
          )
        }
      ]
    }
  ]
};

export default MainRoutes;